import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Boton from "./Boton";
import { IconoTresPuntos } from "./Iconos";
import "./kit.css";

/* ============================================================================
   MENÚ DE ACCIONES (los tres puntos)
   ----------------------------------------------------------------------------
   Regla del sistema: guarda las acciones SECUNDARIAS, nunca la principal. La
   principal siempre es un botón visible al lado.

   POR QUÉ VA EN UN PORTAL (bug real, reportado en pruebas: "los tres puntos no
   se ven"): dentro de una tabla, un panel `position:absolute` queda recortado
   TRES veces -- por `.dt` (overflow:hidden, que es lo que le da el radio a la
   tarjeta), por `.dt__scroll` (overflow-x:auto) y por la propia celda
   (overflow:hidden, que es lo que permite truncar con puntos suspensivos). No
   es un problema de z-index: ninguna de esas tres reglas puede quitarse sin
   romper la tabla. La única salida es sacar el panel del flujo: se pinta en
   <body> con `position:fixed` y coordenadas calculadas desde el disparador.

   Anatomía literal de la referencia: panel de 222px, radio 9, sombra
   `0 10px 30px`, padding 5; ítem de 34px con radio 6, padding 0 10, gap 10,
   texto 13px e icono de 16px; separador de 1px con margen 5px 8px.

   Accesible: `Esc` cierra, el clic fuera cierra, el foco vuelve al disparador
   y las flechas recorren los ítems.
   ========================================================================== */

const ANCHO_PANEL = 222;
const MARGEN = 4;

function MenuAcciones({ acciones = [], titulo = "Más acciones", disabled = false }) {
    const [abierto, setAbierto] = useState(false);
    const [pos, setPos] = useState(null);
    const panelRef = useRef(null);
    const disparadorRef = useRef(null);

    const visibles = acciones.filter(Boolean);

    /** Coloca el panel bajo el disparador, alineado a su derecha, dentro de la ventana. */
    const recolocar = useCallback(() => {
        const boton = disparadorRef.current;
        if (!boton) return;
        const r = boton.getBoundingClientRect();
        const alto = panelRef.current?.offsetHeight || 0;
        const cabeAbajo = r.bottom + MARGEN + alto <= window.innerHeight;
        const izquierda = Math.max(8, Math.min(r.right - ANCHO_PANEL, window.innerWidth - ANCHO_PANEL - 8));
        setPos({
            // Si no cabe debajo, se despliega hacia arriba en vez de salirse.
            top: cabeAbajo ? r.bottom + MARGEN : Math.max(8, r.top - MARGEN - alto),
            left: izquierda,
        });
    }, []);

    useLayoutEffect(() => {
        if (abierto) recolocar();
    }, [abierto, recolocar]);

    useEffect(() => {
        if (!abierto) return undefined;

        const cerrar = () => setAbierto(false);
        const alPulsarFuera = (e) => {
            const dentroDelPanel = panelRef.current && panelRef.current.contains(e.target);
            const enElBoton = disparadorRef.current && disparadorRef.current.contains(e.target);
            if (!dentroDelPanel && !enElBoton) cerrar();
        };
        const alTeclear = (e) => {
            if (e.key === "Escape") {
                cerrar();
                if (disparadorRef.current) disparadorRef.current.focus();
                return;
            }
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
            const items = [...(panelRef.current?.querySelectorAll(".kmenu__item") || [])];
            if (items.length === 0) return;
            e.preventDefault();
            const actual = items.indexOf(document.activeElement);
            const siguiente =
                e.key === "ArrowDown" ? (actual + 1) % items.length : (actual - 1 + items.length) % items.length;
            items[siguiente].focus();
        };

        document.addEventListener("mousedown", alPulsarFuera);
        document.addEventListener("keydown", alTeclear);
        // Al estar en <body>, el panel no se mueve solo con la página: se
        // recoloca mientras siga abierto.
        window.addEventListener("scroll", recolocar, true);
        window.addEventListener("resize", recolocar);
        return () => {
            document.removeEventListener("mousedown", alPulsarFuera);
            document.removeEventListener("keydown", alTeclear);
            window.removeEventListener("scroll", recolocar, true);
            window.removeEventListener("resize", recolocar);
        };
    }, [abierto, recolocar]);

    if (visibles.length === 0) return null;

    const panel = (
        <div
            className="kmenu"
            role="menu"
            ref={panelRef}
            data-testid="menu-acciones"
            style={{ top: pos ? pos.top : -9999, left: pos ? pos.left : -9999 }}
        >
            {visibles.map((accion, i) =>
                accion.separador ? (
                    <span className="kmenu__separador" key={`sep-${i}`} />
                ) : (
                    <button
                        type="button"
                        role="menuitem"
                        key={accion.label}
                        className={`kmenu__item${accion.peligro ? " kmenu__item--peligro" : ""}`}
                        disabled={accion.disabled}
                        onClick={(e) => {
                            e.stopPropagation();
                            setAbierto(false);
                            accion.onClick();
                        }}
                        data-testid={`menu-accion-${accion.key || accion.label}`}
                    >
                        {accion.icono && (
                            <span className="kmenu__item-icono">
                                <accion.icono size={16} />
                            </span>
                        )}
                        {accion.label}
                    </button>
                )
            )}
        </div>
    );

    return (
        <span className="kmenu-envoltorio">
            <Boton
                tamano="sm"
                soloIcono
                icono={IconoTresPuntos}
                titulo={titulo}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={abierto}
                onClick={(e) => {
                    e.stopPropagation();
                    setAbierto((v) => !v);
                }}
                ref={disparadorRef}
                data-testid="menu-acciones-disparador"
            />
            {abierto && createPortal(panel, document.body)}
        </span>
    );
}

export default MenuAcciones;
